
'use client';

import { useAuth } from '@/hooks/useAuth';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Loader2, PlusCircle, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
  } from "@/components/ui/alert-dialog"
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Card } from '@/components/ui/card';
import { Aviso } from '@/lib/types';
import { updateAviso, createAviso, deleteAviso, getAllAvisos } from '@/lib/actions';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function AvisoForm({
  onFormSuccess,
  avisoToEdit,
}: {
  onFormSuccess: () => void;
  avisoToEdit?: Aviso | null;
}) {
  const isEditing = !!avisoToEdit;
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    
    let result;
    if (isEditing && avisoToEdit) {
      result = await updateAviso(avisoToEdit.id, formData);
    } else {
      result = await createAviso(formData);
    }
    
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive',
      });
    }
    setIsSubmitting(false);
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input id="title" name="title" defaultValue={avisoToEdit?.title} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido</Label>
        <RichTextarea
          id="content"
          name="content"
          rows={8}
          defaultValue={avisoToEdit?.content}
          placeholder="Escribe aquí el contenido del aviso..."
        />
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline" type="button">
            Cancelar
          </Button>
        </DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditing ? 'Guardar Cambios' : 'Publicar Aviso'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminAvisosPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [isLoadingAvisos, setIsLoadingAvisos] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [avisoToEdit, setAvisoToEdit] = useState<Aviso | null>(null);
  const { toast } = useToast();

  const fetchAvisos = async () => {
    setIsLoadingAvisos(true);
    const { avisos, error } = await getAllAvisos();
    setAvisos(avisos);
    setError(error);
    setIsLoadingAvisos(false);
  };
  
  useEffect(() => {
    if (!loading && userData?.role === 'admin') {
      fetchAvisos();
    } else if (!loading) {
      navigate('/jartinet');
    }
  }, [user, userData, loading, navigate]);

  const handleFormSuccess = () => {
    setIsDialogOpen(false);
    setAvisoToEdit(null);
    fetchAvisos();
  };
  
  const handleEditClick = (aviso: Aviso) => {
    setAvisoToEdit(aviso);
    setIsDialogOpen(true);
  };

  const handleNewClick = () => {
    setAvisoToEdit(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (avisoId: string) => {
    const result = await deleteAviso(avisoId);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      fetchAvisos();
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
  };

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
       <div className="flex justify-between items-start">
        <div className="max-w-3xl">
          <h1 className="font-headline text-5xl font-bold text-primary">
            Gestionar Avisos
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Crea, edita y elimina los avisos importantes del servidor.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="lg" onClick={handleNewClick}>
              <PlusCircle className="mr-2 h-5 w-5" />
              Nuevo Aviso
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>{avisoToEdit ? 'Editar Aviso' : 'Crear Nuevo Aviso'}</DialogTitle>
              <DialogDescription>
                {avisoToEdit ? 'Modifica los campos necesarios.' : 'Rellena los campos para publicar un nuevo aviso.'}
              </DialogDescription>
            </DialogHeader>
            <AvisoForm onFormSuccess={handleFormSuccess} avisoToEdit={avisoToEdit} />
          </DialogContent>
        </Dialog>
      </div>

       <div className="space-y-4">
        <h2 className="text-2xl font-bold font-headline">Avisos Publicados</h2>
        {isLoadingAvisos ? (
          <div className="flex items-center justify-center p-8"><Loader2 className="animate-spin" /></div>
        ) : error ? (
           <div className="text-center py-16 border-2 border-dashed rounded-lg border-destructive/50">
            <h3 className="mt-4 text-lg font-semibold text-destructive">Error al Cargar Avisos</h3>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          </div>
        ) : avisos && avisos.length > 0 ? (
          <div className="space-y-4">
            {avisos.map(aviso => (
              <Card key={aviso.id} className="flex flex-col sm:flex-row justify-between items-start p-4">
                <div className='flex-grow'>
                    <h3 className="font-bold text-lg">{aviso.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{aviso.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">{format(new Date(aviso.createdAt), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}</p>
                </div>
                <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => handleEditClick(aviso)}>
                        <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar este aviso?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Esta acción es permanente y no se puede deshacer.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(aviso.id)} className="bg-destructive hover:bg-destructive/80">
                                Confirmar Eliminación
                            </AlertDialogAction>
                        </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <h3 className="mt-4 text-lg font-semibold">No hay avisos publicados</h3>
            <p className="mt-1 text-sm text-muted-foreground">Crea el primer aviso para empezar.</p>
          </div>
        )}
      </div>
    </div>
  );
}
